import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageRouteBuilder({ navigate }) {
  const linkCls = 'text-brand-300 hover:text-brand-200 underline underline-offset-2';

  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">
        Editor &mdash; Building a Route
      </h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Building a route means walking the path once yourself while the bot writes it down. There
        is no map editor and no coordinates to type. Everything here is on the{' '}
        <strong className="text-white">AUTOPILOT &rsaquo; Editor</strong> tab; running a finished
        route is on{' '}
        <button onClick={() => navigate('autopilot')} className={linkCls}>AUTOPILOT &rsaquo; Play</button>.
      </p>

      <Callout type="info">
        <strong>The Editor only shows in Advanced mode.</strong> Out of the box the Play tab offers
        ready-made runs and the Editor is hidden. Switch on the <strong>Advanced mode</strong> chip
        at the top of the Play tab, and <strong>Editor</strong> appears in the list on the left.
      </Callout>

      <Callout type="info">
        <strong>The four words you need.</strong>{' '}
        <strong className="text-white">Step</strong> = one map of the route.{' '}
        <strong className="text-white">Path</strong> (the rail) = the line you walked on that map;
        the bot follows it.{' '}
        <strong className="text-white">Exit</strong> = the gate at the end of a step that takes you
        to the next one.{' '}
        <strong className="text-white">Stop</strong> = a marked point on the path where something
        happens (wait, teleport, collect chests).
      </Callout>

      <DocHeading>The Tab at a Glance</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The left column is the route as a whole: the <strong>THE ROUTE</strong> card with the list
        of steps, and under it <strong>START FROM A MAP CHAIN</strong>. The right column,{' '}
        <strong>THIS STEP</strong>, shows the step you clicked in the list. It has three rows
        &mdash; <strong>Path</strong>, <strong>Exit</strong> and <strong>Stops</strong>. A green tick
        means that part is done: the row folds away and shows the result on its right. An orange
        exclamation mark means something is missing, and the row opens by itself. Click a row to
        open or close it.
      </p>
      <p className="text-sm text-gray-400 mb-4">
        There is no separate check card. A problem is written in red inside the row where you fix
        it, and the step&rsquo;s line in the list says <em>no exit</em> or <em>needs a fix</em>.
        Above the cards the tab shows what is going on: a <strong>RECORDING</strong> or{' '}
        <strong>RUNNING</strong> badge with the step number, and the answer to your last button
        press (for example <em>The bot was busy - press it again.</em>).
      </p>

      <Shot
        name="route-builder-01-editor-tab.png"
        tab="AUTOPILOT › Editor"
        caption="The Editor with a three-map Parallel World route: the second step is selected, and you are standing on it."
        shoot="menushot (stage route edit): Advanced mode, a „grimmag merciless” route (Grimmagstone / Alliance Fortress / Fortress Hall), a 2. lépés kijelölve és itt állunk (again + [here]); THIS STEP: Path, Exit, Stops zöld pipával; alatta a START FROM A MAP CHAIN kártya."
      />

      <Callout type="warning">
        <strong>Recording and placing only work on the map you are standing on.</strong> Select a
        step on another map and the Path row says <em>Go to this map to edit it.</em> RECORD, FIX
        FROM HERE, SET EXIT HERE, placing a stop and cutting still click, but only tell you{' '}
        <em>Go to that map first.</em> (or <em>Stop the route first.</em> while a route runs). A
        point recorded from somewhere else would be a meaningless coordinate. Changing what is
        already there works from anywhere: the settings of a stop, REMOVE POINT, the exit&rsquo;s
        Level, REMOVE EXIT &mdash; and CLEAR, so check which step is selected before you press it.
      </Callout>

      <DocHeading>Build a Walking Route</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The example below is a run over several maps, built by hand. It takes two passes: the first
        records the paths, the second sets the exits (the warning after the steps explains why). If
        your run is one of the ready-made map chains, begin with{' '}
        <strong>Start From a Map Chain</strong> further down instead &mdash; it saves the whole
        first pass.
      </p>

      <Step n={1} title="Name the route">
        In <strong>THE ROUTE</strong> type a <strong>Name</strong>. That name is also the file name,
        so use something you will recognise: <em>dl-farm</em>, <em>q7-run</em>. Letters, numbers,
        spaces, - and _ only. <strong>Folder (optional)</strong> sorts it into a folder; leave it
        empty for none.
      </Step>
      <Step n={2} title="Stand on the first map and add it">
        Press <strong>+ ADD THE MAP I AM ON</strong>. The map becomes step 1 and is selected on the
        right.
      </Step>
      <Step n={3} title="Record the path">
        Go to where the run should begin. In the <strong>Path</strong> row press{' '}
        <strong>RECORD</strong> and walk the way you want the bot to walk. Press{' '}
        <strong>STOP</strong> when you reach the gate. A green START and a red END marker appear on
        the ground.
      </Step>
      <Step n={4} title="Go through the gate and repeat">
        On the next map: <strong>+ ADD THE MAP I AM ON</strong> &rarr; <strong>RECORD</strong>{' '}
        &rarr; walk &rarr; <strong>STOP</strong>. Do this for every map of the run.
      </Step>
      <Step n={5} title="Second pass: set the exits">
        Go back to the first map. Click that step in the list, stand next to the gate the bot
        should take, and press <strong>SET EXIT HERE</strong> in the <strong>Exit</strong> row. Go
        through the gate and do the same on the next map. The last step needs no exit.
      </Step>
      <Step n={6} title="Pick the level">
        If the gate offers difficulty levels, choose one in the <strong>Level</strong> dropdown.
        This is not optional &mdash; the run will not start without it.
      </Step>
      <Step n={7} title="Close the loop (optional)">
        On the last step, set the exit to the gate that leads back to the map of step 1, then turn
        on <strong>Loop at the end</strong> on the Play tab to make the run endless. A route without
        an exit on its last step can loop too, but only if its path ends within about 15 m of where
        it started (walk a circle); otherwise the run stops at the end with a message saying so.
      </Step>
      <Step n={8} title="Save">
        When every row shows a green tick, press <strong>SAVE</strong>. Then switch to the Play tab
        and press START.
      </Step>

      <Callout type="warning">
        <strong>An exit remembers where it leads at the moment you press the button</strong>
        &nbsp;&mdash; and only if the next step is already in the list. An exit set before you added
        the next map later says <em>Exit has no target map - set the exit again on that map.</em>{' '}
        The fix is to stand at that gate again and press <strong>MOVE EXIT HERE</strong>. The
        two-pass order above avoids this.
      </Callout>

      <DocHeading>THE ROUTE Card</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Name</strong>, 'The route name and its file name. Until you type one, SAVE only answers "Give the route a name first."'],
          [<strong className="text-white">Folder (optional)</strong>, 'Saves the route into this folder; the folder is created automatically. Empty = no folder. The MY ROUTES list on the Play tab is grouped by these folders.'],
          [<strong className="text-white">Unsaved changes</strong>, 'Shows "yes" while there is something SAVE has not written to disk yet.'],
          [<strong className="text-white">The step list</strong>, 'Click a line to select that step. First line: number and map name. Second line: the map id, points, length in metres and number of stops; a green [here] marks the map you are on. An orange "no exit" or "needs a fix" on the right means the step is not finished.'],
          [<strong className="text-white">go here / again</strong>, 'Shown on steps the bot can teleport straight to (every step of a map chain). It teleports you onto that map so you can record the step. Nothing else happens and no run is started. It reads "again" on the map you are already on. Hidden while a route runs or records.'],
          [<strong className="text-white">+ ADD THE MAP I AM ON</strong>, 'Adds the current map as a new step at the end of the list. If it refuses, it says why: "This map is already a step.", "A route can hold at most 8 maps.", "Stop the route first."'],
          [<strong className="text-white">ADVANCED &rsaquo; UP / DOWN</strong>, 'Moves the selected step in the order.'],
          [<strong className="text-white">ADVANCED &rsaquo; DELETE</strong>, 'Removes the selected step. Press it, then SURE? to confirm.'],
          [<strong className="text-white">SAVE</strong>, 'Writes the route to disk under its Folder and Name. A route with the same name in the same folder is overwritten.'],
          [<strong className="text-white">NEW ROUTE / DISCARD</strong>, 'Both start an empty route. Pressing either one turns the third button into a red confirmation: YES, NEW, or DISCARD + NEW when there are unsaved changes.'],
        ]}
      />
      <Callout type="warning">
        <strong>A map can be in a route only once, and a route holds at most 8 maps.</strong> That
        is how the bot knows which step it is on at any moment. If your run passes the same map
        twice, build it as two routes.
      </Callout>

      <DocHeading>Recording the Path</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        This is the <strong>Path</strong> row of the selected step. When it is done, its summary
        reads <em>N points, X m</em>; otherwise <em>only 1 point</em> or <em>not recorded</em>.
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">RECORD / STOP</strong>, 'Records your walking into the path. Walk it the way you want it walked; the bot follows the same line.'],
          [<strong className="text-white">CLEAR</strong>, 'Throws away the path of this step at once, without asking. If the step has stops, the button reads CLEAR + POINTS and removes those too. Monsters marked with + Kill are not touched.'],
          [<strong className="text-white">FIX FROM HERE</strong>, 'Replaces a bad part of the path without re-recording the whole step. Only shown once the step has a path. See below.'],
          [<strong className="text-white">Show the rail</strong>, 'The chip at the top of the Play tab. Draws the path on the ground as a line of dots (on by default). The part the bot has already passed during a run is faded. Needed if you want to click the path to place stops.'],
          [<strong className="text-white">ADVANCED &rsaquo; Open the map window</strong>, 'A floating map of the current map with the path, the stops and your own position. It stays open when you close the menu, so you can check the path while you walk.'],
          [<strong className="text-white">ADVANCED &rsaquo; Cut between two rail points</strong>, 'The exact editor, for when FIX FROM HERE is not precise enough. See below.'],
        ]}
      />

      <Callout type="info">
        <strong>RECORD adds to the end.</strong> Pressing <strong>RECORD</strong> on a step that
        already has a path does not start it over &mdash; the new walking is appended after the END
        marker. To make a path longer, stand on END first. To record the step from scratch, press{' '}
        <strong>CLEAR</strong> before <strong>RECORD</strong>.
      </Callout>
      <Callout type="warning">
        <strong>Gaps in the path.</strong> If you jump or teleport while recording, or press
        RECORD while standing away from the END marker, the row says{' '}
        <em>N gap(s) in this path.</em> A short gap (up to about 9 m) is crossed in a straight
        line. A wider one stops the run with a message giving its size. Close it with FIX FROM HERE
        or cut it out.
      </Callout>
      <p className="text-sm text-gray-400 mb-4">
        Recording also stops by itself when you leave the map of that step (
        <em>Recording stopped: you left the map of that step.</em>) or when the step cannot hold
        more points (<em>Recording stopped: this step is full (N points).</em> &mdash; the row then
        says <em>Path is FULL.</em>).
      </p>

      <p className="text-sm text-gray-400 mb-2 mt-8"><strong className="text-white">FIX FROM HERE</strong></p>
      <ol className="list-decimal list-inside text-sm text-gray-400 space-y-1.5 mb-4">
        <li>
          Walk to where the route goes wrong and stand on the path (within about 6 m of it). Press{' '}
          <strong>FIX FROM HERE</strong>.
        </li>
        <li>
          Walk the better way. The row shows <em>Fixing from #N - walked N point(s).</em>
        </li>
        <li>
          Where the route is good again, step back onto the path and press <strong>APPLY</strong>.
          Everything between the two spots is replaced. Press <strong>DISCARD</strong> instead to
          keep the path exactly as it was &mdash; nothing changes until you apply.
        </li>
      </ol>
      <p className="text-sm text-gray-400 mb-4">
        Stops that sat on the replaced stretch are removed with it; the message after APPLY says how
        many.
      </p>

      <p className="text-sm text-gray-400 mb-2 mt-8"><strong className="text-white">Cut between two rail points</strong></p>
      <p className="text-sm text-gray-400 mb-4">
        Open <strong>ADVANCED</strong> in the Path row. The list shows the points of the path, each
        with its distance from the start (<em>137.&nbsp;&nbsp;&nbsp;42 m</em>); on a very long path
        not every recorded point gets a line. Click one to pick it and click it
        again to un-pick. The same point lights up on the ground, and you can also pick points by
        clicking the path in the world.
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">CUT BEFORE / CUT AFTER</strong>, 'Deletes everything before / after the picked point. The point itself is kept.'],
          [<strong className="text-white">FROM #N</strong>, 'Makes the picked point one end of a stretch. Then click the point where the stretch ends.'],
          [<strong className="text-white">CUT #A..#B</strong>, 'Deletes the stretch. The two remaining ends are joined by a straight line; the tab tells you how long ("joins in a N m straight line") and how many stops go with it. The stretch is drawn red on the ground until you decide.'],
          [<strong className="text-white">RE-RECORD #A..#B</strong>, 'Walks that stretch again instead of deleting it. Stand on point #A first (within about 6 m); until then the tab tells you how far away you are. Finish with APPLY.'],
          [<strong className="text-white">FORGET THE CUT / START OVER / CLEAR SELECTION</strong>, 'Drop the marked end or the picked point without changing the path.'],
        ]}
      />

      <DocHeading>Setting the Exit</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        This is the <strong>Exit</strong> row. Stand at the gate the bot should take and press{' '}
        <strong>SET EXIT HERE</strong>. The bot takes the nearest gate within 12 m of you, so stand
        close to the right one. Once an exit is set, the button reads{' '}
        <strong>MOVE EXIT HERE</strong>.
      </p>
      <DocTable
        headers={['What you see', 'What it means']}
        rows={[
          [<strong className="text-white">Gate</strong>, 'The gate that was found. A red "not identified" means none was close enough: step next to it and press the button again.'],
          [<strong className="text-white">Leads to</strong>, 'The map of the next step. On the last step it says "nothing after this step". An orange "not set" on any other step means the exit was set before the next step existed (see the warning above).'],
          [<strong className="text-white">Level</strong>, 'Only for gates with several difficulty levels. It says "This gate has N levels - PICK ONE." in red until you pick. The first entry, -- PICK A LEVEL --, is never a valid choice. A gate with a single level says "Only one level - nothing to pick."'],
          [<strong className="text-white">REMOVE EXIT</strong>, 'Deletes the exit of this step.'],
        ]}
      />
      <Callout type="warning">
        <strong>MOVE EXIT HERE clears the level.</strong> On a gate with several levels you have to
        pick it again after moving the exit. On the ground, the label above END shows the chosen
        level, or <em>EXIT - pick a level</em> in red while none is picked.
      </Callout>

      <Callout type="info">
        The last step may have no exit &mdash; the route simply ends there (its summary reads{' '}
        <em>last step - none needed</em>). Every other step needs an exit or a teleport stop that
        leaves the map (<em>a teleport stop leaves this map</em>). Otherwise the next step cannot be
        reached and START is blocked.
      </Callout>

      <DocHeading>Stops (Marked Points)</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        A stop is a place on the path where something should happen. It goes on a point of the
        recorded path that you pick &mdash; <strong className="text-white">not where you are
        standing</strong> &mdash; so you do not have to walk there. You still have to be on that
        step&rsquo;s map.
      </p>
      <DocTable
        headers={['Where', 'How to place a stop']}
        rows={[
          [<strong className="text-white">In the world</strong>, 'With Show the rail on (the chip at the top of the Play tab), point at the path of the step you stand on: the nearest dot lights up with its number and distance. Click it (click it again to un-pick), and a small panel with five buttons appears next to it. Press the type you want. This also works with the menu closed.'],
          [<strong className="text-white">On the tab</strong>, 'Open ADVANCED in the Path row, pick a point in the list, and press a type under "Put a point here:".'],
        ]}
      />

      <p className="text-sm text-gray-400 mb-4">
        The new stop is selected, and you set it up in the <strong>Stops</strong> row. The list there
        shows every stop of this step; click one to see its settings, change its{' '}
        <strong>Type</strong>, or delete it with <strong>REMOVE POINT</strong> (confirm with{' '}
        <strong>REALLY REMOVE</strong>). Clicking a stop on the ground selects it too, and its panel
        lets you change the type or <strong>REMOVE</strong> it &mdash; that one does not ask for
        confirmation. Changing the type clears the item and level you set before.
      </p>
      <DocTable
        headers={['Type', 'In the list', 'What happens there', 'Settings']}
        rows={[
          [<strong className="text-white">Just run</strong>, 'Marker', 'Nothing. It only marks the path for you.', '—'],
          [<strong className="text-white">Wait</strong>, 'Wait', 'Stands still for the time you set.', 'Wait: 1–300 s (5 s when placed)'],
          [<strong className="text-white">Teleport (map)</strong>, 'World map item', 'Uses a world map item from your bag to travel to the map you chose.', 'Item, Region, Map, Level (pick them in this order; a level always has to be picked)'],
          [<strong className="text-white">Teleport (item)</strong>, 'Boss teleport item', 'Uses a teleport item from your bag. The item decides where it goes (shown as Goes to).', 'Item; Level if the destination has several'],
          [<strong className="text-white">Bloodshed chests</strong>, 'Bloodshed chests', 'Waits here for the bloodshed chests, then collects them. Gives up after 60 s if none show up.', '—'],
          [<strong className="text-white">Must kill</strong>, 'Must kill', 'Only in older routes. Tied to one monster, so its type cannot be changed (Must kill (fixed)). For new routes use the + Kill buttons instead.', 'Radius: 1–20 m, Give up after: 1–300 s'],
        ]}
      />
      <Callout type="warning">
        <strong>Teleport items must be in your bag when the route runs.</strong> The{' '}
        <strong>Item</strong> list only offers what is in your bag right now (
        <em>No teleport items in your bag right now.</em>). If the item is gone later, the Stops row
        says <em>Teleport item &lsquo;X&rsquo; is not in your bag.</em> and START is blocked. If it
        sits somewhere other than a normal bag slot, the message tells you to move it there.
      </Callout>
      <Callout type="info">
        <strong>Warnings in the list.</strong> <em>N m off the path</em> means the stop is too far
        from the path to be reached &mdash; usually after the path was re-recorded. Remove it and
        place it again on the new path. <em>needs a fix - see above</em> means the red text above
        the list says what is missing, for example <em>No item picked for this point.</em>
      </Callout>
      <p className="text-sm text-gray-400 mb-4">
        In the Path row&rsquo;s ADVANCED list, picking a point that already has a stop shows{' '}
        <strong>Marked point here</strong> with its type buttons, <strong>REMOVE POINT</strong> and{' '}
        <strong>OPEN IN MARKED POINTS</strong>, which selects that stop in the Stops row.
      </p>

      <DocHeading>Marking Monsters to Kill</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Monsters are not stops on the path: a kill target belongs to the whole map of a step. Turn
        on <strong>+Kill buttons on monsters</strong> on the{' '}
        <button onClick={() => navigate('autopilot')} className={linkCls}>Play tab</button> (it is on
        by default). While you stand on a map that is a step of the loaded route, every monster in
        the world gets a small button.
      </p>
      <DocTable
        headers={['Button', 'What it does']}
        rows={[
          [<strong className="text-white">+ Kill</strong>, 'Adds that kind of monster to this step’s kill list.'],
          [<strong className="text-white">Remove</strong>, 'Shown on monsters that are already on the list. Takes them off again.'],
          [<strong className="text-white">Hovering the button</strong>, 'Shows the monster’s name and rank. A leader and a normal monster of the same kind are separate entries. If it says "the game says this cannot be targeted - a helper object, not a real monster", do not mark it.'],
        ]}
      />
      <Callout type="info">
        The kill list is not shown on the Editor tab. You see it in the world (the button says{' '}
        <strong>Remove</strong>) and as the kill count in the map window. CLEAR does not touch it.
        For the bot to actually fight these monsters, turn on{' '}
        <strong>Also the route&rsquo;s kill targets</strong> on{' '}
        <button onClick={() => navigate('auto-combat')} className={linkCls}>AUTOPILOT &rsaquo; Combat</button>.
        No buttons at all? The map you are on is not a step yet &mdash; add it first.
      </Callout>

      <DocHeading>Start From a Map Chain</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong>START FROM A MAP CHAIN</strong> card loads a ready-made chain of maps. A map chain
        already runs: load it, press START on the Play tab from anywhere, and the bot teleports in
        and moves through the maps and gates by itself. What it does not have is a path inside the
        maps and any stops &mdash; those you record on top, step by step.
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Category</strong>, 'Blood Chest, Event, Parallel World or World Bosses.'],
          [<strong className="text-white">Map</strong>, 'The chain within that category. One line under the dropdowns says what it does, for example "Three maps, then it loops - Khalys at the end".'],
          [<strong className="text-white">Colour</strong>, 'Only for chains that come in several colours (Kia Krisal).'],
          [<strong className="text-white">Difficulty</strong>, 'The level to run. It starts on the hardest one.'],
          [<strong className="text-white">LOAD AS A NEW ROUTE</strong>, 'Replaces the route in the editor with this chain.'],
        ]}
      />
      <Callout type="warning">
        <strong>LOAD AS A NEW ROUTE does not ask first.</strong> Save the route you are working on
        before you press it. The loaded chain is not saved either &mdash; press SAVE if you want to
        keep it together with the paths you record.
      </Callout>
      <DocTable
        headers={['Category', 'Chains']}
        rows={[
          ['Blood Chest', 'Lingering Memory'],
          ['Event', "Sargon's Throne Room, Dracanian Challenge Arena, Kia Krisal"],
          ['Parallel World', 'Grimmagstone, Hagastove Grotto, Stonekeep Island, Wildherz Caverns, Ruins of the First Circle, Fortress Teganswall, Hailstone Mountains, Mount Suvius, Ship Graveyard'],
          ['World Bosses', "Mortis' Exile, Varnok Tower, Primal Spark, Azar's Pit"],
        ]}
      />
      <p className="text-sm text-gray-400 mb-2 mt-6"><strong className="text-white">Adding your own path to a chain</strong></p>
      <p className="text-sm text-gray-400 mb-4">
        Click a step in the list and press <strong>go here</strong>: you are teleported onto that map
        without starting a run. <strong>RECORD</strong> the path you want walked there, add stops
        (for example <strong>Bloodshed chests</strong>), then <strong>SAVE</strong>. A step with a
        recorded path is walked; a step without one is only passed through. For Parallel World
        chains, <strong>Boss room only</strong> on the Play tab decides whether each lap goes back
        through all three maps.
      </p>

      <Callout type="info">
        If the tab says{' '}
        <em>This is the built-in key farm route - it cannot be edited or saved.</em>, the editor
        holds the route loaded by{' '}
        <button onClick={() => navigate('key-farm')} className={linkCls}>AUTOPILOT &rsaquo; Key farm</button>.
        To build or edit your own, press NEW ROUTE, load a map chain, or load a saved route on the
        Play tab.
      </Callout>

      <DocHeading>Problems and What They Mean</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-red-400">Red</strong> messages block START; the Play tab then says{' '}
        <em>N PROBLEMS - see the Editor tab</em>. Each one appears inside the row where you fix it,
        and a problem with a stop starts with <em>Stop N:</em>. If there are too many to list, THE
        ROUTE card says <em>There are more problems than fit - fix these first.</em> These are the
        ones you will actually meet:
      </p>
      <DocTable
        headers={['Message', 'What to do']}
        rows={[
          ['The route has no name.', 'Type a Name in THE ROUTE.'],
          ['The route has no maps in it yet.', 'Stand on a map and press + ADD THE MAP I AM ON.'],
          ['No path recorded on this map yet.', 'Select that step, go to that map, and RECORD.'],
          ["Map 'X' is already step N.", 'The same map was added twice. Delete one of the two steps.'],
          ['No exit set, so the route cannot reach step N.', 'Stand at the gate on that step and press SET EXIT HERE.'],
          ['Exit was not identified - stand next to it and set it again.', 'Step right next to the gate and press MOVE EXIT HERE.'],
          ['Exit has no target map - set the exit again on that map.', 'The exit was set before the next step existed. Stand at the gate and press MOVE EXIT HERE.'],
          ["Exit leads to 'X' but step N is 'Y'.", 'The gate goes somewhere other than the next step. Reorder the steps (ADVANCED › UP / DOWN) or set a different exit.'],
          ['This exit has N difficulty levels - pick one and save.', 'Pick a Level in the Exit row, then SAVE.'],
          ['No item picked for this point. / No destination set for this teleport point.', 'Open that stop in the Stops row and pick its Item (and Region / Map for a world map item).'],
          ['Pick which level of this map to travel to. / This teleport has N difficulty levels - pick one.', 'Pick a Level for that teleport stop.'],
          ["Teleport item 'X' is not in your bag.", 'Put the item back into your bag, or change the stop.'],
          ['Must-kill point has no monster on it.', 'Remove that stop and mark the monster with + Kill instead.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-amber-300">Orange</strong> notes do not block START:{' '}
        <em>N gap(s) in this path.</em> (see Gaps above), <em>Path is FULL.</em> (the recording
        stopped at its limit, so the end of a very long path may be missing), and{' '}
        <em>N m off the path</em> on a stop.
      </p>

      <DocHeading>Editing a Route Later</DocHeading>
      <DocTable
        headers={['You want to...', 'Do this']}
        rows={[
          ['Change one stretch of the path', 'Stand where it goes wrong, press FIX FROM HERE, walk the better way, then APPLY.'],
          ['Make the path longer at the end', 'Stand on the END marker, press RECORD, walk on, then STOP.'],
          ['Start one map over', 'Select the step, press CLEAR, then RECORD again.'],
          ['Add a map in the middle', 'Add it (it lands at the end), move it with ADVANCED › UP / DOWN, set its exit, and press MOVE EXIT HERE at the gate of the step before it.'],
          ['Change the difficulty', 'Select the step and pick another Level in the Exit row.'],
          ['Record one step of a long chain', 'Press go here on that step instead of walking the whole chain.'],
          ['Keep both versions', 'Type a different Name (or Folder) and press SAVE. The old file stays.'],
        ]}
      />
      <Callout type="info">
        When you are done, switch to{' '}
        <button onClick={() => navigate('autopilot')} className={linkCls}>AUTOPILOT &rsaquo; Play</button>{' '}
        and press START. Your saved routes are listed there too.
      </Callout>
    </div>
  );
}

export default PageRouteBuilder;
